/**
 * whiteboardBaselineStore — persistence for board versions (the merge base).
 *
 * When a whiteboard is imported it's snapshotted as v1; a later "re-capture"
 * diffs against the stored baseline and saves v2. This is the bookkeeping that
 * makes the round-trip durable across sessions and devices.
 *
 * System of record: the `whiteboard_boards` Supabase table (migration 527),
 * workspace-scoped via RLS. localStorage is kept as a write-through cache and
 * offline fallback so the flow still works when Supabase isn't configured or
 * the table hasn't been migrated yet (reads/writes degrade gracefully).
 */

import { isSupabaseAvailable, fetchAll, fetchById, upsertRecord, deleteRecord, updateRecord, getCurrentWorkspaceId } from './supabaseSync';
import { deleteBoardCapture } from './whiteboardCaptureStore';
import type { BoardBaseline } from './whiteboardMerge';

export interface WhiteboardBoard {
  boardId: string;
  title: string;
  versionNumber: number;
  updatedAt: string;
  baseline: BoardBaseline;
  /** Public Supabase Storage URLs for the last capture tile(s). */
  captureImageUrls?: string[];
}

/** camelCase row shape — supabaseSync maps these to/from snake_case columns. */
interface BoardRow {
  id: string;
  workspaceId?: string;
  title: string;
  versionNumber: number;
  baseline: BoardBaseline;
  updatedAt?: string;
  captureImageUrls?: string[];
}

const TABLE = 'whiteboard_boards';
const STORAGE_KEY = 'whiteboard_boards_v1';

// ── localStorage cache / fallback ────────────────────────────────────────────

function readCache(): Record<string, WhiteboardBoard> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, WhiteboardBoard>) : {};
  } catch {
    return {};
  }
}

function writeCache(map: Record<string, WhiteboardBoard>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* quota / disabled — cache is best-effort */
  }
}

function cacheUpsert(board: WhiteboardBoard): void {
  const all = readCache();
  all[board.boardId] = board;
  writeCache(all);
}

function cacheList(): WhiteboardBoard[] {
  return Object.values(readCache()).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

function rowToBoard(row: BoardRow): WhiteboardBoard {
  const baseline =
    typeof row.baseline === 'string' ? (JSON.parse(row.baseline) as BoardBaseline) : row.baseline;
  return {
    boardId: row.id,
    title: row.title,
    versionNumber: row.versionNumber,
    updatedAt: row.updatedAt ?? new Date().toISOString(),
    baseline,
    ...(row.captureImageUrls?.length ? { captureImageUrls: row.captureImageUrls } : {}),
  };
}

// ── Public async API ─────────────────────────────────────────────────────────

/** All boards for the current workspace, newest first. Falls back to cache. */
export async function loadBoards(): Promise<WhiteboardBoard[]> {
  if (isSupabaseAvailable()) {
    const { data, error } = await fetchAll<BoardRow>(
      TABLE,
      { column: 'updated_at', ascending: false },
      { filterByWorkspace: true },
    );
    if (!error && data) {
      const boards = data.map(rowToBoard);
      const map: Record<string, WhiteboardBoard> = {};
      for (const b of boards) map[b.boardId] = b;
      writeCache(map);
      return boards;
    }
  }
  return cacheList();
}

export async function getBoard(boardId: string): Promise<WhiteboardBoard | null> {
  if (isSupabaseAvailable()) {
    const { data, error } = await fetchById<BoardRow>(TABLE, boardId);
    if (!error && data) {
      const board = rowToBoard(data);
      cacheUpsert(board);
      return board;
    }
  }
  return readCache()[boardId] ?? null;
}

/**
 * Save (or version-bump) a board's baseline. On first save versionNumber is 1;
 * each subsequent save for the same boardId increments it. Writes through to the
 * cache immediately and to Supabase when available.
 */
export async function saveBoardBaseline(
  boardId: string,
  title: string,
  baseline: BoardBaseline,
): Promise<WhiteboardBoard> {
  const prev = await getBoard(boardId);
  const board: WhiteboardBoard = {
    boardId,
    title: title.trim() || prev?.title || 'Whiteboard',
    versionNumber: (prev?.versionNumber ?? 0) + 1,
    updatedAt: new Date().toISOString(),
    baseline,
    ...(prev?.captureImageUrls?.length ? { captureImageUrls: prev.captureImageUrls } : {}),
  };
  cacheUpsert(board);

  if (isSupabaseAvailable()) {
    const workspaceId = getCurrentWorkspaceId() ?? undefined;
    const row: BoardRow = {
      id: boardId,
      ...(workspaceId && { workspaceId }),
      title: board.title,
      versionNumber: board.versionNumber,
      baseline,
      updatedAt: board.updatedAt,
      captureImageUrls: board.captureImageUrls ?? [],
    };
    const { error } = await upsertRecord<BoardRow>(TABLE, row);
    if (error) {
      console.warn('[whiteboardBaselineStore] cloud save failed, kept in local cache:', error);
    }
  }

  return board;
}

/** Persist capture photo URLs on the board row (cloud + local cache). */
export async function patchBoardCaptureUrls(boardId: string, captureImageUrls: string[]): Promise<void> {
  const prev = readCache()[boardId] ?? (await getBoard(boardId));
  if (prev) {
    cacheUpsert({ ...prev, captureImageUrls, updatedAt: new Date().toISOString() });
  }
  if (isSupabaseAvailable()) {
    const { error } = await updateRecord(TABLE, boardId, { captureImageUrls });
    if (error) {
      console.warn('[whiteboardBaselineStore] capture URL patch failed:', error);
    }
  }
}

/** Remove a board (both the local cache and, when available, Supabase). Used by import undo. */
export async function deleteBoard(boardId: string): Promise<void> {
  const all = readCache();
  if (all[boardId]) {
    delete all[boardId];
    writeCache(all);
  }
  if (isSupabaseAvailable()) {
    const { error } = await deleteRecord(TABLE, boardId);
    if (error) console.warn('[whiteboardBaselineStore] cloud delete failed:', error);
  }
  void deleteBoardCapture(boardId);
}
